import { ref } from 'vue'
import Cookies from 'js-cookie'
import { auth, functions, httpsCallable, setPersistence, browserLocalPersistence, signInWithCustomToken, getIdToken, sendPasswordResetEmail } from '../js/firebaseConfig'

export function useAuth() {
  const loading = ref(false)
  const error = ref(null)

  function getFriendlyErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address.'
      case 'auth/invalid-credential':
        return 'Invalid email address or password.'
      case 'auth/user-disabled':
        return 'This user account has been disabled.'
      case 'auth/user-not-found':
        return 'No user found with this email address.'
      case 'auth/wrong-password':
        return 'Incorrect password.'
      case 'auth/email-already-in-use':
        return 'Email address is already in use.'
      case 'auth/weak-password':
        return 'The password is too weak.'
      case 'auth/too-many-requests':
        return 'Too many login attempts. Please try again later.'
      default:
        return 'An error occurred. Please try again.'
    }
  }

  async function setCookie(user, companyId, isAdmin, isTrainer) {
    Cookies.set('userMail', user.email)
    Cookies.set('companyId', companyId)
    
    const idToken = await getIdToken(auth.currentUser, true)
    Cookies.set('idToken', idToken, { secure: true, sameSite: 'strict' })
    
    if (isAdmin) {
      Cookies.set('admin', 'true', { secure: true, sameSite: 'strict' })
    } else {
      Cookies.remove('admin')
    }
    if (isTrainer) {
      Cookies.set('trainer', 'true', { secure: true, sameSite: 'strict' })
    } else {
      Cookies.remove('trainer')
    }
    
    console.log('✅ [Login] Cookies sécurisés définis, redirection...')
    window.location.href = 'dashboard.html'
  }

  async function login(email, password) {
    if (loading.value) return { success: false }
    
    loading.value = true
    error.value = null
    
    try {
      const normalizedEmail = email.toLowerCase().trim()
      console.log('🔐 [Login] Tentative de connexion...')
      
      const secureLogin = httpsCallable(functions, 'secureLogin')
      const result = await secureLogin({ email: normalizedEmail, password: password })
      
      if (!result.data.success) {
        throw new Error('Échec de la connexion')
      }
      
      const { customToken, uid, email: userEmail, companyId, isAdmin, isTrainer } = result.data
      
      console.log('✅ [Login] Token personnalisé reçu, authentification en cours...')
      
      await setPersistence(auth, browserLocalPersistence)
      console.log('✅ [Login] Persistance LOCAL configurée')
      
      const userCredential = await signInWithCustomToken(auth, customToken)
      const user = userCredential.user
      
      console.log('✅ [Login] Connexion réussie')
      console.log('🔐 [Login] Rôles - Admin:', isAdmin, 'Trainer:', isTrainer)
      
      await setCookie(user, companyId, isAdmin, isTrainer)
      
      return { success: true }
    } catch (err) {
      console.error('❌ [Login] Erreur de connexion', err)
      
      let friendlyMessage = 'Email ou mot de passe incorrect'
      
      if (err.code === 'functions/unauthenticated') {
        friendlyMessage = 'Email ou mot de passe incorrect'
      } else if (err.code === 'functions/resource-exhausted') {
        friendlyMessage = err.message || 'Trop de tentatives. Veuillez réessayer dans 15 minutes.'
      } else if (err.code === 'functions/permission-denied') {
        friendlyMessage = 'Ce compte a été désactivé. Contactez le support.'
      } else if (err.code === 'functions/failed-precondition') {
        friendlyMessage = 'Erreur de configuration du compte. Contactez le support.'
      } else if (err.code === 'functions/internal') {
        friendlyMessage = 'Une erreur est survenue. Veuillez réessayer.'
      }
      
      error.value = friendlyMessage
      return { success: false, error: friendlyMessage }
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(email) {
    if (!email) {
      return { success: false, error: 'Veuillez entrer votre adresse email.' }
    }
    
    try {
      await sendPasswordResetEmail(auth, email)
      return { success: true, message: 'Email de réinitialisation de mot de passe envoyé ! Veuillez vérifier votre boîte de réception.' }
    } catch (err) {
      const friendlyMessage = getFriendlyErrorMessage(err.code)
      console.error('Error sending password reset email: ', err)
      return { success: false, error: friendlyMessage }
    }
  }

  return {
    loading,
    error,
    login,
    resetPassword,
    getFriendlyErrorMessage
  }
}
